import { Dispatch, SetStateAction, useCallback } from 'react';

import ProjectDate from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/dates/project-date';
import BroadcastTypePicker from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/broadcast-type-picker';
import Container from '@ui/container/container';
import handleBroadcastTypeChange from './helpers/handle-broadcast-type-change';
import { Session, SessionTypesEnum } from '@/types/session.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { DateRange } from '@ui/calendar/types';
import { saveWorkingProjectByFields, saveWorkingProjectSessionByField } from '@/store/slices/project';
import getSelectedBroadcastType from './helpers/get-selected-broadcast-type';
import { getUser } from '@/store/slices/user/selectors';
import {
  getWorkingProjectSessions,
  selectProjectSettings,
  selectWorkingProjectDefaultLanguage,
  selectWorkingProjectLanguages,
} from '@/store/slices/project/selectors';

type TFocusedSessionInfoContentProps = {
  session?: Session | null;
  selectedDates: {
    startDate: number | null;
    endDate: number | null;
    selectedTimezone: string;
  };
  isFocusedWithin: boolean;
  setSelectedDates: Dispatch<
    SetStateAction<{
      startDate: number | null;
      endDate: number | null;
      selectedTimezone: string;
    }>
  >;
  syncProjectDates?: boolean;
};

const FocusedSessionInfoContent = ({
  session,
  selectedDates,
  setSelectedDates,
  isFocusedWithin,
  syncProjectDates,
}: TFocusedSessionInfoContentProps) => {
  const user = useAppSelector(getUser);
  const sessions = useAppSelector(getWorkingProjectSessions);
  const projectLanguages = useAppSelector(selectWorkingProjectLanguages);
  const projectDefaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const workingProjectSettings = useAppSelector(selectProjectSettings);

  const dispatch = useAppDispatch();

  const startTimestamp = session?.timestamp;
  const endTimestamp = session?.end_timestamp;
  const isBreakout = session?.session_type === SessionTypesEnum.breakoutRooms;

  const handleDateChange = useCallback(
    ({ startDate, endDate }: DateRange) => {
      if (!startDate || !endDate || !session) return;

      // Update the project dates in the store or database
      setSelectedDates((prev) => ({ startDate: null, endDate: null, selectedTimezone: prev.selectedTimezone }));

      dispatch(
        saveWorkingProjectSessionByField({
          uuid: session.uuid,
          timestamp: startDate,
          end_timestamp: endDate,
          requires_time_change: null,
        })
      );

      if (syncProjectDates) {
        dispatch(
          saveWorkingProjectByFields({
            start_timestamp: startDate,
            end_timestamp: endDate,
          })
        );
      }
    },
    [dispatch, session, setSelectedDates, syncProjectDates]
  );

  if (!session) return null;
  return (
    <Container
      width="100%"
      className={`position-absolute top-0 left-0 flex ai-flex-end gap-12 ${isFocusedWithin ? '' : 'pointer-events-none'}`}
      opacity={isFocusedWithin ? 1 : 0}
      transitionDuration="0ms"
      data-component="FocusedSessionInfoContent"
      minHeight="108px"
    >
      <Container className="flex-1">
        <ProjectDate
          hideLabel
          timestamp={startTimestamp}
          end_timestamp={endTimestamp}
          selectedTimezone={selectedDates.selectedTimezone}
          isInEditMode={isFocusedWithin}
          onChange={setSelectedDates}
          requiresTimeChange={session?.requires_time_change}
          onClose={() => {
            if (selectedDates?.startDate && selectedDates.endDate) {
              handleDateChange({
                startDate: selectedDates.startDate,
                endDate: selectedDates.endDate,
                selectedTimezone: selectedDates.selectedTimezone,
              });
            }
          }}
        />
      </Container>

      <Container>
        <BroadcastTypePicker
          selected={getSelectedBroadcastType(session)}
          onChange={(type) =>
            handleBroadcastTypeChange({
              type,
              sessions,
              session,
              user,
              projectLanguages,
              projectDefaultLanguage,
              dispatch,
              workingProjectSettings,
            })
          }
          disabled={isBreakout}
          allowFireside
          isEditingInfo={isFocusedWithin}
        />
      </Container>
    </Container>
  );
};

export default FocusedSessionInfoContent;
